import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useUserOrganization } from './useOrganization';
import { subDays, startOfDay, endOfDay, format } from 'date-fns';

export interface DNSQueryStats {
  totalBlocksToday: number;
  totalBlocksWeek: number;
  extensionBlocksToday: number;
  extensionBlocksWeek: number;
  dnsBlocksToday: number;
  dnsBlocksWeek: number;
  activeRules: number;
  mostBlockedSite: string;
  streakDays: number;
}

export interface BlockedActivity {
  date: string;
  count: number;
  extensionBlocks: number;
  dnsBlocks: number;
  topSites: string[];
  timeDistribution: {
    hour: number;
    blocks: number;
    extensionBlocks: number;
    dnsBlocks: number;
  }[];
}

function timeRangeToDate(timeRange: string): Date {
  const now = new Date();
  switch (timeRange) {
    case '24h': return subDays(now, 1);
    case '30d': return subDays(now, 30);
    case '90d': return subDays(now, 90);
    default:    return subDays(now, 7);
  }
}

export function useDNSQueryStats(timeRange: string = '7d') {
  const { data: organization } = useUserOrganization();

  return useQuery({
    queryKey: ['dns-query-stats', organization?.id, timeRange],
    queryFn: async (): Promise<DNSQueryStats> => {
      // Try to get stats from extension first (same as popup)
      try {
        if (typeof chrome !== 'undefined' && chrome.runtime && chrome.runtime.sendMessage) {
          const response = await chrome.runtime.sendMessage({ type: "GET_STATUS" });
          
          if (response?.stats) {
            return {
              totalBlocksToday: response.stats.blockedToday || 0,
              totalBlocksWeek: response.stats.totalBlocked || 0,
              extensionBlocksToday: response.stats.blockedToday || 0,
              extensionBlocksWeek: response.stats.totalBlocked || 0,
              dnsBlocksToday: 0,
              dnsBlocksWeek: 0,
              activeRules: 0,
              mostBlockedSite: '',
              streakDays: 0,
            };
          }
        }
      } catch (error) {
        console.error('Failed to get stats from extension:', error);
      }
      
      // Fallback to database query
      if (!organization?.id) {
        return {
          totalBlocksToday: 0,
          totalBlocksWeek: 0,
          extensionBlocksToday: 0,
          extensionBlocksWeek: 0,
          dnsBlocksToday: 0,
          dnsBlocksWeek: 0,
          activeRules: 0,
          mostBlockedSite: '',
          streakDays: 0,
        };
      }
      
      const now = new Date();
      const todayStart = startOfDay(now);
      const rangeStart = startOfDay(timeRangeToDate(timeRange));

      // Get all blocks today
      const { data: todayBlocks } = await supabase
        .from('dns_queries')
        .select('id, query_type')
        .eq('organization_id', organization.id)
        .eq('action', 'blocked')
        .gte('timestamp', todayStart.toISOString());

      // Get all blocks in the selected range
      const { data: weekBlocks } = await supabase
        .from('dns_queries')
        .select('id, query_type')
        .eq('organization_id', organization.id)
        .eq('action', 'blocked')
        .gte('timestamp', rangeStart.toISOString());
      
      // Separate extension blocks (query_type: 'A') from DNS server blocks (other types)
      // Extension blocks typically use 'A' records, DNS server uses various types
      const todayExtensionBlocks = todayBlocks?.filter(block => block.query_type === 'A') || [];
      const todayDnsBlocks = todayBlocks?.filter(block => block.query_type !== 'A') || [];
      const weekExtensionBlocks = weekBlocks?.filter(block => block.query_type === 'A') || [];
      const weekDnsBlocks = weekBlocks?.filter(block => block.query_type !== 'A') || [];
      
      // Get active policies count
      const { data: activePolicies } = await supabase
        .from('dns_policies')
        .select('id')
        .eq('organization_id', organization.id)
        .eq('is_active', true);
      
      // Get most blocked site
      const { data: topBlockedSites } = await supabase
        .from('dns_queries')
        .select('domain')
        .eq('organization_id', organization.id)
        .eq('action', 'blocked')
        .gte('timestamp', rangeStart.toISOString());
      
      const domainCounts: Record<string, number> = {};
      topBlockedSites?.forEach(query => {
        domainCounts[query.domain] = (domainCounts[query.domain] || 0) + 1;
      });
      
      const mostBlockedSite = Object.entries(domainCounts)
        .sort(([,a], [,b]) => b - a)[0]?.[0] || '';
      
      // Calculate streak (consecutive days with any blocks - extension or DNS)
      let streakDays = 0;
      for (let i = 0; i < 30; i++) {
        const dayStart = startOfDay(subDays(now, i));
        const dayEnd = endOfDay(dayStart);
        
        const { data: dayBlocks } = await supabase
          .from('dns_queries')
          .select('id')
          .eq('organization_id', organization.id)
          .eq('action', 'blocked')
          .gte('timestamp', dayStart.toISOString())
          .lte('timestamp', dayEnd.toISOString())
          .limit(1);
        
        if (dayBlocks && dayBlocks.length > 0) {
          streakDays++;
        } else {
          // Break on first day with no blocks (streak ends)
          break;
        }
      }
      
      return {
        totalBlocksToday: todayBlocks?.length || 0,
        totalBlocksWeek: weekBlocks?.length || 0,
        extensionBlocksToday: todayExtensionBlocks.length,
        extensionBlocksWeek: weekExtensionBlocks.length,
        dnsBlocksToday: todayDnsBlocks.length,
        dnsBlocksWeek: weekDnsBlocks.length,
        activeRules: activePolicies?.length || 0,
        mostBlockedSite,
        streakDays,
      };
    },
    enabled: !!organization?.id,
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
  });
}

export interface RecentBlock {
  id: string;
  domain: string;
  blocked_category: string | null;
  timestamp: string;
  query_type: string | null;
  source: 'extension' | 'dns';
}

export function useRecentBlocks(limit: number = 20) {
  const { data: organization } = useUserOrganization();
  
  return useQuery({
    queryKey: ['recent-blocks', organization?.id, limit],
    queryFn: async (): Promise<RecentBlock[]> => {
      console.log('🔧 Fetching recent blocks, organization:', organization?.id);
      
      // First, try to get data from extension if in extension context
      try {
        // Check if we're in a Chrome extension context
        if (typeof chrome !== 'undefined' && chrome.runtime && chrome.runtime.sendMessage) {
          console.log('🔧 Attempting to fetch from extension background script');
          // Get data from extension background script (same as popup)
          const response = await chrome.runtime.sendMessage({
            type: "GET_RECENT_BLOCKS",
          });
          
          console.log('🔧 Extension response:', response);
          
          if (response?.blocks) {
            const mappedBlocks = response.blocks.slice(0, limit).map((block: any) => ({
              id: block.domain + block.timestamp, // Generate ID
              domain: block.domain,
              blocked_category: block.reason || 'Blocked',
              timestamp: new Date(block.timestamp).toISOString(),
              query_type: 'A', // Default to A record
              source: 'extension' as const
            }));
            console.log('🔧 Extension blocks mapped:', mappedBlocks);
            return mappedBlocks;
          }
        }
      } catch (error) {
        console.warn('Failed to get recent blocks from extension, falling back to database:', error);
      }
      
      // Fallback to direct database query for web dashboard or when extension is not available
      if (!organization?.id) {
        console.log('🔧 No organization ID, returning empty array');
        return [];
      }
      
      console.log('🔧 Fetching from database for organization:', organization.id);
      
      // Let's also check what the current time is for debugging
      const now = new Date();
      console.log('🔧 Current time:', now.toISOString());
      
      const { data: recentBlocks, error } = await supabase
        .from('dns_queries')
        .select('id, domain, blocked_category, timestamp, query_type, action')
        .eq('organization_id', organization.id)
        .eq('action', 'blocked')
        .order('timestamp', { ascending: false })
        .limit(limit);
      
      if (error) {
        console.error('❌ Error fetching recent blocks:', error);
        return [];
      }
      
      console.log('🔧 Database query result:', recentBlocks);
      console.log('🔧 Query organization ID:', organization.id);
      console.log('🔧 Query action filter:', 'blocked');
      console.log('🔧 Query limit:', limit);
      
      if (!recentBlocks) {
        console.log('🔧 No recent blocks found in database (null result)');
        return [];
      }
      
      // Additional debugging - check if we're filtering out valid data
      console.log('🔧 Raw query result length:', recentBlocks.length);
      
      const mappedBlocks = recentBlocks.map(block => ({
        ...block,
        source: block.query_type === 'A' ? 'extension' as const : 'dns' as const
      }));
      
      console.log('🔧 Database blocks mapped:', mappedBlocks);
      console.log('🔧 Final mapped blocks length:', mappedBlocks.length);
      
      return mappedBlocks;
    },
    enabled: !!organization?.id, // Only enable when we have an organization
    refetchInterval: 30 * 1000, // Refetch every 30 seconds for real-time updates
  });
}

export function useDNSActivityData(timeRange: string = '7d') {
  const { data: organization } = useUserOrganization();

  return useQuery({
    queryKey: ['dns-activity-data', organization?.id, timeRange],
    queryFn: async (): Promise<BlockedActivity[]> => {
      if (!organization?.id) return [];

      const activities: BlockedActivity[] = [];
      const now = new Date();

      // Cap at 30 data points to avoid excessive per-day queries
      const days = Math.min(
        timeRange === '24h' ? 1 : timeRange === '30d' ? 30 : timeRange === '90d' ? 30 : 7,
        30
      );

      for (let i = days - 1; i >= 0; i--) {
        const date = subDays(now, i);
        const dayStart = startOfDay(date);
        const dayEnd = endOfDay(date);
        const dateStr = format(date, 'yyyy-MM-dd');
        
        // Get all blocks for this day
        const { data: dayBlocks } = await supabase
          .from('dns_queries')
          .select('domain, timestamp, query_type')
          .eq('organization_id', organization.id)
          .eq('action', 'blocked')
          .gte('timestamp', dayStart.toISOString())
          .lte('timestamp', dayEnd.toISOString());
        
        if (!dayBlocks) {
          activities.push({
            date: dateStr,
            count: 0,
            extensionBlocks: 0,
            dnsBlocks: 0,
            topSites: [],
            timeDistribution: Array.from({ length: 24 }, (_, hour) => ({
              hour, 
              blocks: 0,
              extensionBlocks: 0,
              dnsBlocks: 0
            })),
          });
          continue;
        }
        
        // Separate extension vs DNS blocks
        const extensionBlocks = dayBlocks.filter(block => block.query_type === 'A');
        const dnsBlocks = dayBlocks.filter(block => block.query_type !== 'A');
        
        // Count by domain
        const domainCounts: Record<string, number> = {};
        const hourCounts: Record<number, number> = {};
        const hourExtensionCounts: Record<number, number> = {};
        const hourDnsCounts: Record<number, number> = {};
        
        dayBlocks.forEach(block => {
          domainCounts[block.domain] = (domainCounts[block.domain] || 0) + 1;
          
          const hour = new Date(block.timestamp).getHours();
          hourCounts[hour] = (hourCounts[hour] || 0) + 1;
          
          if (block.query_type === 'A') {
            hourExtensionCounts[hour] = (hourExtensionCounts[hour] || 0) + 1;
          } else {
            hourDnsCounts[hour] = (hourDnsCounts[hour] || 0) + 1;
          }
        });
        
        // Get top 3 sites
        const topSites = Object.entries(domainCounts)
          .sort(([,a], [,b]) => b - a)
          .slice(0, 3)
          .map(([domain]) => domain);
        
        // Create time distribution
        const timeDistribution = Array.from({ length: 24 }, (_, hour) => ({
          hour,
          blocks: hourCounts[hour] || 0,
          extensionBlocks: hourExtensionCounts[hour] || 0,
          dnsBlocks: hourDnsCounts[hour] || 0,
        }));
        
        activities.push({
          date: dateStr,
          count: dayBlocks.length,
          extensionBlocks: extensionBlocks.length,
          dnsBlocks: dnsBlocks.length,
          topSites,
          timeDistribution,
        });
      }
      
      return activities;
    },
    enabled: !!organization?.id,
    staleTime: 10 * 60 * 1000,
  });
}

export interface ThreatData {
  threatCounts: Record<string, number>;
  topBlocked: { domain: string; blocks: number }[];
}

export function useDNSThreatData(timeRange: string = '7d') {
  const { data: organization } = useUserOrganization();

  return useQuery({
    queryKey: ['dns-threat-data', organization?.id, timeRange],
    queryFn: async (): Promise<ThreatData> => {
      if (!organization?.id) return { threatCounts: {}, topBlocked: [] };

      const since = timeRangeToDate(timeRange);

      const { data } = await supabase
        .from('dns_queries')
        .select('domain, blocked_category')
        .eq('organization_id', organization.id)
        .eq('action', 'blocked')
        .gte('timestamp', since.toISOString());

      if (!data || data.length === 0) return { threatCounts: {}, topBlocked: [] };

      const threatCounts: Record<string, number> = {};
      const domainCounts: Record<string, number> = {};

      data.forEach(row => {
        const cat = row.blocked_category || 'uncategorized';
        threatCounts[cat] = (threatCounts[cat] || 0) + 1;
        domainCounts[row.domain] = (domainCounts[row.domain] || 0) + 1;
      });

      const topBlocked = Object.entries(domainCounts)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 5)
        .map(([domain, blocks]) => ({ domain, blocks }));

      return { threatCounts, topBlocked };
    },
    enabled: !!organization?.id,
  });
}